/**
 * Configuration déclarative pour l'entité Crew
 * Source unique de vérité pour tous les champs de Crew
 */

import { defineEntityConfig } from '../../lib/entity-config';
import type { Crew, CrewFinancingType, CrewProbability, CrewStatus } from '../../types/crew';
import {
  CREW_FINANCING_TYPE,
  CREW_FINANCING_TYPE_LABELS,
  CREW_PROBABILITY,
  CREW_PROBABILITY_LABELS,
  CREW_STATUS,
  CREW_STATUS_LABELS,
} from '../../types/crew';

export const CREW_CONFIG = defineEntityConfig<Crew>({
  entityName: 'équipage',
  entityNamePlural: 'équipages',

  fields: {
    crew_number: {
      key: 'crew_number',
      label: 'Numéro',
      type: 'text',
      sortable: true,
      searchable: true,
      showInTable: true,
      showInFilters: false,
      exportable: true,
      importable: true,
      size: 100,
    },

    name: {
      key: 'name',
      label: 'Nom',
      type: 'text',
      required: false,
      sortable: true,
      searchable: true,
      showInTable: true,
      showInFilters: true,
      exportable: true,
      importable: true,
      size: 200,
    },

    status: {
      key: 'status',
      label: 'Statut',
      type: 'enum',
      enumValues: Object.values(CREW_STATUS) as any,
      required: true,
      sortable: true,
      editable: true,
      showInTable: true,
      showInFilters: true,
      exportable: true,
      importable: true,
      size: 180,
      exportTransform: (value: CrewStatus) => CREW_STATUS_LABELS[value] || value,
      importTransform: (value: string) => {
        const normalized = value.toLowerCase().trim();
        if (
          normalized === 'bpi en attente' ||
          normalized === 'bpi_payment_pending' ||
          normalized === 'paiement bpi en attente'
        )
          return CREW_STATUS.BPI_PAYMENT_PENDING;
        if (
          normalized === 'pré-inscrit' ||
          normalized === 'pre-inscrit' ||
          normalized === 'pre_registered'
        )
          return CREW_STATUS.PRE_REGISTERED;
        if (normalized === 'bed téléchargé' || normalized === 'bed telecharge')
          return CREW_STATUS.BED_DOWNLOADED;
        if (normalized === 'bed_downloaded') return CREW_STATUS.BED_DOWNLOADED;
        if (normalized === 'bed incomplet — paiement' || normalized === 'bed incomplet - paiement')
          return CREW_STATUS.BED_INCOMPLETE_PAYMENT;
        if (normalized === 'bed_incomplete_payment') return CREW_STATUS.BED_INCOMPLETE_PAYMENT;
        // Le libellé historique « BED incomplet » (avant 00178, sans distinction
        // de cause) reste accepté et retombe sur « infos manquantes ».
        if (
          normalized === 'bed incomplet' ||
          normalized === 'bed incomplet — infos' ||
          normalized === 'bed incomplet - infos' ||
          normalized === 'bed_incomplete'
        )
          return CREW_STATUS.BED_INCOMPLETE;
        if (normalized === 'bed complet' || normalized === 'bed_complete')
          return CREW_STATUS.BED_COMPLETE;
        if (normalized === 'annulation conforme' || normalized === 'cancellation_compliant')
          return CREW_STATUS.CANCELLATION_COMPLIANT;
        if (normalized === 'annulation non conforme' || normalized === 'cancellation_non_compliant')
          return CREW_STATUS.CANCELLATION_NON_COMPLIANT;
        if (normalized === 'report conforme' || normalized === 'postponement_compliant')
          return CREW_STATUS.POSTPONEMENT_COMPLIANT;
        if (normalized === 'report non conforme' || normalized === 'postponement_non_compliant')
          return CREW_STATUS.POSTPONEMENT_NON_COMPLIANT;
        if (
          normalized === 'terminé' ||
          normalized === 'completed' ||
          normalized === 'inscrit définitif'
        )
          return CREW_STATUS.COMPLETED;
        if (normalized === 'archivé' || normalized === 'archived') return CREW_STATUS.ARCHIVED;
        return CREW_STATUS.PRE_REGISTERED; // Défaut
      },
    },

    budget: {
      key: 'budget',
      label: 'Budget',
      type: 'number',
      sortable: true,
      showInTable: true,
      showInFilters: false,
      exportable: true,
      importable: true,
      size: 120,
      exportTransform: (value: number | null) => {
        if (!value) return '-';
        return new Intl.NumberFormat('fr-FR', {
          style: 'currency',
          currency: 'EUR',
        }).format(value);
      },
    },

    probability: {
      key: 'probability',
      label: 'Probabilité',
      type: 'enum',
      enumValues: Object.values(CREW_PROBABILITY) as any,
      required: true,
      sortable: true,
      editable: true,
      showInTable: true,
      showInFilters: true,
      exportable: true,
      importable: true,
      size: 150,
      exportTransform: (value: CrewProbability) => CREW_PROBABILITY_LABELS[value] || value,
      importTransform: (value: string) => {
        const normalized = value.toLowerCase();
        if (normalized === 'faible' || normalized === 'low') return CREW_PROBABILITY.LOW;
        if (normalized === 'moyenne' || normalized === 'medium') return CREW_PROBABILITY.MEDIUM;
        if (normalized === 'forte' || normalized === 'high') return CREW_PROBABILITY.HIGH;
        return CREW_PROBABILITY.MEDIUM; // Défaut
      },
    },

    assigned_user: {
      key: 'assigned_user',
      label: 'Assigné à',
      type: 'relation',
      sortable: false,
      showInTable: true,
      showInFilters: false,
      exportable: true,
      importable: false,
      size: 150,
      exportTransform: (user: any) => user?.full_name || user?.email || '',
    },

    created_at: {
      key: 'created_at',
      label: 'Date de création',
      type: 'date',
      sortable: true,
      showInTable: true,
      showInFilters: false,
      exportable: true,
      importable: false,
      size: 120,
      exportTransform: (value: string) =>
        value ? new Date(value).toLocaleDateString('fr-FR') : '-',
    },

    product: {
      key: 'product',
      label: 'Produit',
      type: 'relation',
      sortable: false,
      showInTable: false,
      showInFilters: false,
      exportable: true,
      importable: false,
      exportTransform: (product: any) => product?.name || '',
    },

    vehicle: {
      key: 'vehicle',
      label: 'Véhicule',
      type: 'relation',
      sortable: false,
      showInTable: false,
      showInFilters: false,
      exportable: true,
      importable: false,
      exportTransform: (vehicle: any) => vehicle?.registration || '',
    },

    association: {
      key: 'association',
      label: 'Association',
      type: 'relation',
      sortable: false,
      showInTable: false,
      showInFilters: false,
      exportable: true,
      importable: false,
      exportTransform: (association: any) => association?.name || '',
    },

    discount: {
      key: 'discount',
      label: 'Réduction',
      type: 'relation',
      sortable: false,
      showInTable: false,
      showInFilters: false,
      exportable: true,
      importable: false,
      exportTransform: (discount: any) => discount?.name || '',
    },

    financing_type: {
      key: 'financing_type',
      label: 'Type de financement',
      type: 'enum',
      enumValues: Object.values(CREW_FINANCING_TYPE) as any,
      required: true,
      sortable: false,
      showInTable: false,
      showInFilters: false,
      exportable: true,
      importable: true,
      exportTransform: (value: CrewFinancingType) => CREW_FINANCING_TYPE_LABELS[value] || value,
    },

    report: {
      key: 'report',
      label: 'Motif annulation/report',
      type: 'text',
      sortable: false,
      showInTable: false,
      showInFilters: false,
      exportable: true,
      importable: true,
      exportTransform: (value: string | null) => value || '',
    },

    has_derogation: {
      key: 'has_derogation',
      label: 'Dérogation',
      type: 'boolean',
      sortable: false,
      showInTable: false,
      showInFilters: false,
      exportable: true,
      importable: true,
      exportTransform: (value: boolean) => (value ? 'Oui' : 'Non'),
    },

    // V2 R4 Phase B : déclaration "possède un véhicule"
    has_vehicle: {
      key: 'has_vehicle',
      label: 'Possède un véhicule',
      type: 'boolean',
      sortable: false,
      showInTable: false,
      showInFilters: false,
      exportable: true,
      importable: true,
      exportTransform: (value: boolean) => (value ? 'Oui' : 'Non'),
    },

    organizes_pvd: {
      key: 'organizes_pvd',
      label: 'Organise PVD',
      type: 'boolean',
      sortable: false,
      showInTable: false,
      showInFilters: false,
      exportable: true,
      importable: true,
      exportTransform: (value: boolean) => (value ? 'Oui' : 'Non'),
    },

    members_names: {
      key: 'members',
      label: 'Membres',
      type: 'relation',
      sortable: false,
      showInTable: true,
      showInFilters: false,
      exportable: true,
      importable: false,
      size: 250,
      exportTransform: (members: any) => {
        if (!members || !Array.isArray(members)) return '';
        return members
          .map((m: any) => `${m.first_name || ''} ${m.last_name || ''}`.trim())
          .filter(Boolean)
          .join(', ');
      },
    },

    // Colonnes d'export enrichi pour pilote
    pilot_first_name: {
      key: 'members',
      label: 'Pilote - Prénom',
      type: 'relation',
      sortable: false,
      showInTable: false,
      hiddenInColumnSelector: true,
      showInFilters: false,
      exportable: true,
      importable: false,
      exportTransform: (members: any) => {
        if (!members || !Array.isArray(members)) return '';
        // Chercher le pilote par rôle, sinon prendre le 1er membre
        const pilot = members.find((m: any) => m.role === 'pilot') || members[0];
        return pilot?.first_name || '';
      },
    },

    pilot_last_name: {
      key: 'members',
      label: 'Pilote - Nom',
      type: 'relation',
      sortable: false,
      showInTable: false,
      hiddenInColumnSelector: true,
      showInFilters: false,
      exportable: true,
      importable: false,
      exportTransform: (members: any) => {
        if (!members || !Array.isArray(members)) return '';
        const pilot = members.find((m: any) => m.role === 'pilot') || members[0];
        return pilot?.last_name || '';
      },
    },

    pilot_email: {
      key: 'members',
      label: 'Pilote - Email',
      type: 'relation',
      sortable: false,
      showInTable: false,
      hiddenInColumnSelector: true,
      showInFilters: false,
      exportable: true,
      importable: false,
      exportTransform: (members: any) => {
        if (!members || !Array.isArray(members)) return '';
        const pilot = members.find((m: any) => m.role === 'pilot') || members[0];
        return pilot?.email || '';
      },
    },

    pilot_phone: {
      key: 'members',
      label: 'Pilote - Téléphone',
      type: 'relation',
      sortable: false,
      showInTable: false,
      hiddenInColumnSelector: true,
      showInFilters: false,
      exportable: true,
      importable: false,
      exportTransform: (members: any) => {
        if (!members || !Array.isArray(members)) return '';
        const pilot = members.find((m: any) => m.role === 'pilot') || members[0];
        return pilot?.phone || '';
      },
    },

    // Item #4 : code postal du pilote (affichable + filtrable via vue crews_filter_view)
    pilot_postal_code: {
      key: 'pilot_postal_code',
      label: 'Pilote - Code postal',
      type: 'text',
      sortable: true,
      showInTable: false,
      hiddenInColumnSelector: false,
      showInFilters: true,
      exportable: true,
      importable: false,
    },

    // Item #4 : ville du pilote (affichable + filtrable via vue crews_filter_view)
    pilot_city: {
      key: 'pilot_city',
      label: 'Pilote - Ville',
      type: 'text',
      sortable: true,
      showInTable: false,
      hiddenInColumnSelector: false,
      showInFilters: true,
      exportable: true,
      importable: false,
    },

    // Colonnes d'export enrichi pour copilote
    copilot_first_name: {
      key: 'members',
      label: 'Copilote - Prénom',
      type: 'relation',
      sortable: false,
      showInTable: false,
      hiddenInColumnSelector: true,
      showInFilters: false,
      exportable: true,
      importable: false,
      exportTransform: (members: any) => {
        if (!members || !Array.isArray(members)) return '';
        // Chercher le copilote par rôle, sinon prendre le 2ème membre
        const copilot = members.find((m: any) => m.role === 'copilot') || members[1];
        return copilot?.first_name || '';
      },
    },

    copilot_last_name: {
      key: 'members',
      label: 'Copilote - Nom',
      type: 'relation',
      sortable: false,
      showInTable: false,
      hiddenInColumnSelector: true,
      showInFilters: false,
      exportable: true,
      importable: false,
      exportTransform: (members: any) => {
        if (!members || !Array.isArray(members)) return '';
        const copilot = members.find((m: any) => m.role === 'copilot') || members[1];
        return copilot?.last_name || '';
      },
    },

    copilot_email: {
      key: 'members',
      label: 'Copilote - Email',
      type: 'relation',
      sortable: false,
      showInTable: false,
      hiddenInColumnSelector: true,
      showInFilters: false,
      exportable: true,
      importable: false,
      exportTransform: (members: any) => {
        if (!members || !Array.isArray(members)) return '';
        const copilot = members.find((m: any) => m.role === 'copilot') || members[1];
        return copilot?.email || '';
      },
    },

    copilot_phone: {
      key: 'members',
      label: 'Copilote - Téléphone',
      type: 'relation',
      sortable: false,
      showInTable: false,
      hiddenInColumnSelector: true,
      showInFilters: false,
      exportable: true,
      importable: false,
      exportTransform: (members: any) => {
        if (!members || !Array.isArray(members)) return '';
        const copilot = members.find((m: any) => m.role === 'copilot') || members[1];
        return copilot?.phone || '';
      },
    },

    notes: {
      key: 'notes',
      label: 'Notes',
      type: 'text',
      sortable: false,
      showInTable: false,
      showInFilters: false,
      exportable: true,
      importable: true,
      exportTransform: (value: string | null) => value || '',
    },

    // Champs techniques masqués du sélecteur de colonnes
    updated_at: {
      key: 'updated_at',
      label: 'Mis à jour le',
      type: 'date',
      sortable: false,
      showInTable: false,
      hiddenInColumnSelector: true,
      showInFilters: false,
      exportable: false,
      importable: false,
    },

    // Champ virtuel (crews_filter_view) — searchable (n° BPI + n° BED via
    // substitution BED→BPI, cf. postgrestSearch) ET colonne « N° BPI »
    // disponible dans le sélecteur (retour réunion 07/07 E). Masquée par défaut
    // (showInTable: false), exportable.
    bpi_code: {
      key: 'bpi_code',
      label: 'N° BPI',
      type: 'text',
      searchable: true,
      showInTable: false,
      hiddenInColumnSelector: false,
      showInFilters: false,
      exportable: true,
      importable: false,
      size: 140,
    },

    // ===== Colonnes BED (migration 00178, cahier L131-136) =====
    // Alimentées par `get_crews_bed_summary` sur le chemin d'export uniquement
    // (cf. useCrews.fetchFiltered). Le STATUT BED est déjà exporté par la
    // colonne « Statut » : les statuts BED sont les statuts d'équipage.
    bed_missing_labels: {
      key: 'bed_missing_labels',
      label: 'Éléments manquants BED',
      type: 'text',
      sortable: false,
      showInTable: false,
      hiddenInColumnSelector: true,
      showInFilters: false,
      exportable: true,
      importable: false,
      // Colonne de synthèse : « Passeport (Pilote) · Permis (Copilote) ».
      exportTransform: (value: string | null) => value || '',
    },

    bed_missing_count: {
      key: 'bed_missing_count',
      label: 'Nb éléments manquants BED',
      type: 'number',
      sortable: false,
      showInTable: false,
      hiddenInColumnSelector: true,
      showInFilters: false,
      exportable: true,
      importable: false,
      exportTransform: (value: number | null) =>
        value === null || value === undefined ? '' : String(value),
    },

    bed_remaining_cents: {
      key: 'bed_remaining_cents',
      label: 'Reste à payer BED',
      type: 'number',
      sortable: false,
      showInTable: false,
      hiddenInColumnSelector: true,
      showInFilters: false,
      exportable: true,
      importable: false,
      exportTransform: (value: number | null) => {
        if (value === null || value === undefined) return '';
        return new Intl.NumberFormat('fr-FR', {
          style: 'currency',
          currency: 'EUR',
        }).format(value / 100);
      },
    },
  },
});
